<template>
  <header class="app-header">
    <div class="header-container">
      <div class="header-content">
        <!-- Logo and Brand -->
        <router-link to="/dashboard" class="brand-link">
          <div class="brand">
            <img
              class="brand-logo"
              src="../assets/logo.svg"
              alt="Mail Client Logo"
            />
            <h1 class="brand-name">Fuli Mail Client</h1>
          </div>
        </router-link>

        <!-- Actions Menu -->
        <div class="user-actions">
          <!-- User dropdown -->
          <div class="user-dropdown">
            <button
              @click="toggleProfileMenu"
              class="user-avatar-button"
              id="user-menu"
              aria-haspopup="true"
              :aria-expanded="isProfileMenuOpen"
            >
              <span class="sr-only">Open user menu</span>
              <div
                class="user-avatar"
                :style="{ backgroundColor: getUserColor(username) }"
              >
                <span class="user-initials">{{ userInitials }}</span>
              </div>
            </button>

            <!-- Dropdown Menu -->
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="isProfileMenuOpen"
                class="dropdown-menu"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu"
              >
                <div class="dropdown-header">
                  <div class="user-name">{{ username }}</div>
                </div>

                <div class="dropdown-divider"></div>
                <a
                  href="#"
                  class="dropdown-item"
                  role="menuitem"
                  @click.prevent="logout"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="item-icon"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span>Sign out</span>
                </a>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { TokenManager } from "@/services/api";

export default defineComponent({
  name: "NavigationBar",
  setup() {
    const store = useStore();
    const router = useRouter();
    const isProfileMenuOpen = ref(false);

    const username = computed(() => store.getters.username || "User");

    const userInitials = computed(() => {
      const name = username.value;
      if (!name) return "?";

      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
    });

    // Generate a consistent color for a user
    const getUserColor = (name) => {
      if (!name) return "#3b82f6";

      // A list of pleasant, accessible colors
      const colors = [
        "#3b82f6", // Blue
        "#8b5cf6", // Violet
        "#ec4899", // Pink
        "#f97316", // Orange
        "#14b8a6", // Teal
        "#f59e0b", // Amber
        "#10b981", // Emerald
        "#6366f1", // Indigo
        "#ef4444", // Red
        "#84cc16", // Lime
      ];

      // Simple hash function to get consistent colors for the same name
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    };

    const toggleProfileMenu = () => {
      isProfileMenuOpen.value = !isProfileMenuOpen.value;
    };

    const closeProfileMenu = (e) => {
      // Close dropdown when clicking outside
      if (isProfileMenuOpen.value && !e.target.closest(".user-dropdown")) {
        isProfileMenuOpen.value = false;
      }
    };

    const navigateToSettings = () => {
      router.push({ name: "Settings" });
      isProfileMenuOpen.value = false;
    };

    const logout = async () => {
      try {
        isProfileMenuOpen.value = false;

        // Clear tokens immediately to prevent any race conditions
        TokenManager.clearTokens();

        // Dispatch logout action to clear store state
        await store.dispatch("logout");

        // Navigate to login page
        await router.push({ name: "Login" });
      } catch (error) {
        console.error("Logout error:", error);

        // Even if store logout fails, ensure tokens are cleared and redirect
        TokenManager.clearTokens();

        // Force navigation to login page
        try {
          await router.push({ name: "Login" });
        } catch (routerError) {
          // If router fails, use window.location as fallback
          console.error(
            "Router navigation failed, using window.location:",
            routerError
          );
          window.location.href = "/login";
        }
      }
    };

    // Close dropdown when clicking outside
    onMounted(() => {
      document.addEventListener("click", closeProfileMenu);
    });

    onUnmounted(() => {
      document.removeEventListener("click", closeProfileMenu);
    });

    return {
      isProfileMenuOpen,
      username,
      userInitials,
      getUserColor,
      toggleProfileMenu,
      navigateToSettings,
      logout,
    };
  },
});
</script>

<style lang="scss">
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$primary-light: #eff6ff;

$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #6b7280;
$text-light: #9ca3af;
$text-white: #ffffff;

$border-color: #e5e7eb;
$border-dark: #d1d5db;

$bg-white: #ffffff;
$bg-light: #f9fafb;
$bg-hover: #f3f4f6;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
  0 2px 4px -1px rgba(0, 0, 0, 0.06);
$shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  0 4px 6px -2px rgba(0, 0, 0, 0.05);
$shadow-dropdown: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  0 4px 6px -2px rgba(0, 0, 0, 0.05);

$radius-sm: 0.25rem;
$radius: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;
$radius-full: 9999px;

$transition: all 0.2s ease;

$header-height: 4rem;

// Screen breakpoints
$screen-sm: 640px;
$screen-md: 768px;
$screen-lg: 1024px;
$screen-xl: 1280px;

// App header
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: $header-height;
  background-color: $bg-white;
  border-bottom: 1px solid $border-color;
  box-shadow: $shadow-sm;
  z-index: 1000;
}

.header-container {
  max-width: 1280px;
  margin: 0 auto;
  height: 100%;
  padding: 0 1.25rem;

  @media (min-width: $screen-sm) {
    padding: 0 1.5rem;
  }

  @media (min-width: $screen-lg) {
    padding: 0 2rem;
  }
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

// Brand section
.brand {
  display: flex;
  align-items: center;
}

.brand-logo {
  height: 2rem;
  width: auto;
}

.brand-name {
  margin: 0 0 0 0.75rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: $text-primary;

  @media (max-width: $screen-md) {
    font-size: 1rem;
  }

  @media (max-width: $screen-sm) {
    display: none;
  }
}

// User actions
.user-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

// User dropdown
.user-dropdown {
  position: relative;
}

.user-avatar-button {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: $radius-full;
  transition: $transition;

  &:hover {
    background-color: $bg-light;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px $bg-white, 0 0 0 4px rgba($primary-color, 0.5);
  }
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: $radius-full;
  color: $text-white;
  font-weight: 600;

  @media (max-width: $screen-sm) {
    width: 2.25rem;
    height: 2.25rem;
  }
}

.user-initials {
  font-size: 1rem;
  line-height: 1;
}

// Dropdown menu
.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 15rem;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-dropdown;
  border: 1px solid $border-color;
  overflow: hidden;
  z-index: 50;
}

.dropdown-header {
  padding: 1rem;
  background-color: $bg-light;
}

.user-name {
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 0.25rem;
}

.user-role {
  font-size: 0.875rem;
  color: $text-secondary;
}

.dropdown-divider {
  height: 1px;
  background-color: $border-color;
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: $text-secondary;
  font-size: 0.875rem;
  text-decoration: none;
  transition: $transition;

  .item-icon {
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.75rem;
    flex-shrink: 0;
  }

  &:hover {
    background-color: $bg-hover;
    color: $text-primary;
  }
}

// Utility class for screen readers
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

// Animation classes
.transition {
  transition-property: opacity, transform;
}

.duration-75 {
  transition-duration: 75ms;
}

.duration-100 {
  transition-duration: 100ms;
}

.ease-in {
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.ease-out {
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}

.transform {
  transform: translateX(var(--tw-translate-x)) translateY(var(--tw-translate-y))
    rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y))
    scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

.opacity-0 {
  opacity: 0;
}

.opacity-100 {
  opacity: 1;
}

.scale-95 {
  --tw-scale-x: 0.95;
  --tw-scale-y: 0.95;
}

.scale-100 {
  --tw-scale-x: 1;
  --tw-scale-y: 1;
}

.brand-link {
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: $transition;

  &:hover {
    opacity: 0.9;
  }
}
</style>
